/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { ReactNode, useEffect, useMemo } from 'react';
import {
  breakpoints,
  Button,
  Paragraph,
  Select,
  Space,
  Text,
} from 'components';
import { AppointmentType, ListingAvailability } from '@bb/domain/dist/types';
import styled from 'styled-components';
import { find, memoize, trim } from 'lodash';
import { ListingLabelContext } from 'contexts';
import {
  HOME_INSPECTION_TYPES,
  T_HOME_INSPECTION_TYPES,
} from '@app/listing/constants';
import { ListingDoc } from '@bb/domain/dist/schemas';
import type { JsonObject } from '@/app-package';
import { NoMoreShowings, NoMoreShowingsProps } from './NoMoreShowings';

export type OverrideOptionType = Extract<
  AppointmentType,
  | 'Home Inspection'
  | 'Appraisal'
  | T_HOME_INSPECTION_TYPES
  | 'Buyer Visit'
  | 'Photographer'
  | 'Pest Control'
  | 'Final Walkthrough'
>;

type AppointmentOverrideProps = {
  listing: JsonObject<ListingDoc>;
  handleOverride: (selectedType: OverrideOptionType) => void;
  selectedOverrideType: OverrideOptionType;
  noMoreShowings: boolean;
  isExternal: boolean;
  setIsBookingAllowed: (isBookingAllowed: boolean) => void;
} & NoMoreShowingsProps;

export enum ShowingScreenType {
  Forbid = 'Forbid', // no booking allowed at all
  AdminOverride = 'AdminOverride', // (internal only) users with permission can just override && book
  AgentOverride = 'AgentOverride', // users without permission can select a reason to override && book
  Default = 'Default', // just go to normal booking flow, no override needed.
}

export const getShowingScreenType = (() => {
  // follow the path in the tree until a decision(leaf) is found.
  const decide = (tree: any, path: string[]): ShowingScreenType => {
    if (typeof tree !== 'object') return tree;
    const [key, ...rest] = path;
    const nextBranch =
      tree[key] ||
      find(tree, (_, k) => k.split('|').map(trim).includes(key)) ||
      tree.default;
    return decide(nextBranch, rest);
  };

  // Exhaustive list of all possible override types.
  const DECISION_TREE: any = {
    canEdit: {
      default: {
        noMoreShowings: ShowingScreenType.AdminOverride,
        default: ShowingScreenType.Default,
      },
    },
    default: {
      'Sold | Leased | Pending': ShowingScreenType.AgentOverride,
      'Leased Conditionally | Sold Conditionally': {
        noMoreShowings: ShowingScreenType.AgentOverride,
        default: ShowingScreenType.Default,
      },
      'Suspended | Terminated | Expired': ShowingScreenType.Forbid,
      default: {
        noMoreShowings: ShowingScreenType.Forbid,
        default: ShowingScreenType.Default,
      },
    },
  };

  return memoize(
    (
      canEdit: boolean,
      listingAvailability: ListingAvailability,
      noMoreShowings: boolean,
    ) => {
      const path = [
        canEdit ? 'canEdit' : 'default',
        listingAvailability,
        noMoreShowings ? 'noMoreShowings' : 'default',
      ];
      return decide(DECISION_TREE, path);
    },
    (...args) => args.join('|'),
  );
})();

const ForbidBooking: React.FC<{
  listing: JsonObject<ListingDoc>;
  availability: string;
  isExternal?: boolean;
  children?: ReactNode;
}> = ({ listing, isExternal, availability, children }) => {
  const { getAvailability } = React.useContext(ListingLabelContext);

  return isExternal || children ? (
    <StyledSpace direction="vertical">
      <Paragraph>
        This property is currently not accepting online showings.
        <Paragraph>
          Listing Status: {getAvailability(listing.availability)}
        </Paragraph>
      </Paragraph>
      {children ?? (
        <Paragraph>
          For more information, please contact the listing agent.
        </Paragraph>
      )}
    </StyledSpace>
  ) : (
    <NoMoreShowings
      listing={listing}
      availability={availability}
      allowOverride={false}
    />
  );
};

const AdminOverride: React.FC<NoMoreShowingsProps> = ({ ...props }) => <NoMoreShowings allowOverride {...props} />;

const AgentOverride: React.FC<AppointmentOverrideProps> = (() => {
  const OVERRIDE_OPTIONS: { label: string; value: OverrideOptionType }[] = [
    { label: 'I need to book an Appraisal Visit', value: 'Appraisal' },
    {
      label: 'I need to book a Home Inspection Visit',
      value: 'Home Inspection',
    },
    ...HOME_INSPECTION_TYPES.map((value: OverrideOptionType) => ({
      label: `I need to book a ${value}`,
      value,
    })),
    { label: 'I need to book a Photographer Visit', value: 'Photographer' },
    { label: 'I need to book a Pest Control Visit', value: 'Pest Control' },
    {
      label: 'I need to book a Final Walk-Through with my buyers',
      value: 'Final Walkthrough',
    },
  ];

  return ({ listing, handleOverride, selectedOverrideType, ...props }) => {
    const defaultValue = useMemo(
      () =>
        OVERRIDE_OPTIONS.find(({ value }) => value === selectedOverrideType) ||
        undefined,
      [selectedOverrideType],
    );

    const [selected, setSelected] =
      React.useState<OverrideOptionType>(selectedOverrideType);

    return (
      <ForbidBooking listing={listing} {...props}>
        <Paragraph>
          If you were involved in this transaction representing the buyers and
          are required to gain access to the property, please select from one of
          the following reasons.
        </Paragraph>
        <div>
          <StyledLabel type="secondary">
            Reason for showing request:
          </StyledLabel>
          <StyledSelect
            defaultValue={defaultValue}
            options={OVERRIDE_OPTIONS}
            labelInValue
            onChange={option => {
              setSelected(option.value);
            }}
            style={{ maxWidth: breakpoints.sm, marginBottom: 5 }}
          />
        </div>
        <StyledButton
          type="primary"
          onClick={() => handleOverride(selected)}
          disabled={!selected}
        >
          Continue
        </StyledButton>
      </ForbidBooking>
    );
  };
})();

export const AppointmentOverride: React.FC<
  AppointmentOverrideProps
> = props => {
  const { listing, noMoreShowings, setIsBookingAllowed } = props;

  useEffect(() => {
    setIsBookingAllowed(false);
    return () => setIsBookingAllowed(true);
  }, [setIsBookingAllowed]);

  switch (
    getShowingScreenType(
      listing?.canEdit,
      listing?.availability,
      noMoreShowings,
    )
  ) {
    case ShowingScreenType.Forbid:
      return <ForbidBooking {...props} />;
    case ShowingScreenType.AdminOverride:
      return <AdminOverride {...props} />;
    case ShowingScreenType.AgentOverride:
      return <AgentOverride {...props} />;
    default:
      return <></>;
  }
};

export default AppointmentOverride;

const StyledSelect = styled(Select)`
  max-width: ${breakpoints.sm};
  margin-bottom: 5px;
`;

const StyledLabel = styled(Text)`
  display: block;
  margin-bottom: 5px;
`;

const StyledButton = styled(Button)`
  // 👇🏼 default antd styles that are overridden by angular style sheets, that we have to override them back 🤷🏼‍♂️
  &&& {
    color: #fff;
    :disabled {
      color: rgba(0, 0, 0, 0.25);
      cursor: not-allowed;
    }
  }
`;

const StyledSpace = styled(Space)`
  max-width: 720px;
  margin-left: 25px;
  margin-top: 15px;
  font-size: 14px;
`;
